import { gql, useQuery } from '@apollo/client';
import type { GraphQLSchema } from 'graphql';
import React from 'react';

import type { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { buildSchemaLeniently } from 'src/lib/buildSchemaLeniently';
import { BuildGraphQLSchemaError } from 'src/lib/errors/buildSchemaError/BuildSchemaError';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';
import type { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

const studioIntrospectionQuery = gql`
  query Studio__IntrospectionQuery($serviceId: ID!, $schemaTag: String!) {
    service(id: $serviceId) {
      id
      graphType
      schema(tag: $schemaTag) {
        document
        createdAt
        hash
      }
      variant(name: $schemaTag) {
        id
        activeSchemaPublish {
          id
          compositionResult {
            graphCompositionID
            supergraphSdl
          }
        }
      }
    }
  }
`;

/**
 * Get the GraphQLSchema result from the registry service.
 *
 * The return type here should try to mimic `QueryResult`, but it doesn't
 * because some of the extra stuff returned from `useQuery` is typed, so using
 * `QueryResult<IntrospectionQuery>` causes type mismatches.
 */
export function useSchemaFromRegistry({
  graphRef,
  skip,
}: {
  graphRef: GraphRef | null;
  skip: boolean;
}): {
  loading: boolean;
  error: Error | undefined;
  schema: GraphQLSchema | undefined;
  supergraphSchema: GraphQLSchema | undefined;
  supergraphSchemaDocument: string | undefined;
  schemaDocument: string | undefined;
  createdAt: Timestamp['output'] | undefined;
  hash: string | undefined;
} {
  const { loading, error, data } = useQuery<
    GraphQLTypes.Studio__IntrospectionQuery,
    GraphQLTypes.Studio__IntrospectionQueryVariables
  >(studioIntrospectionQuery, {
    skip,
    variables: {
      serviceId: graphRef?.graphId ?? '',
      schemaTag: graphRef?.graphVariant ?? '',
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const supergraphSchemaDocument =
    data?.service?.variant?.activeSchemaPublish?.compositionResult
      ?.supergraphSdl ?? undefined;

  const schemaDocument = data?.service?.schema?.document;

  type SchemaOrError = {
    schema?: GraphQLSchema;
    error?: BuildGraphQLSchemaError;
  };

  const { schema, error: buildSchemaError } =
    React.useMemo<SchemaOrError>(() => {
      if (!schemaDocument) {
        return {};
      }

      try {
        const _schema = buildSchemaLeniently(schemaDocument).schema;
        return { schema: _schema };
      } catch (e) {
        if (e instanceof BuildGraphQLSchemaError) {
          return { error: e };
        } else {
          // rethrow unexpected errors
          throw e;
        }
      }
    }, [schemaDocument]);

  const { schema: supergraphSchema, error: supergraphSchemaError } =
    React.useMemo<SchemaOrError>(() => {
      if (!supergraphSchemaDocument) {
        return {};
      }

      try {
        const _schema = buildSchemaLeniently(supergraphSchemaDocument).schema;
        return { schema: _schema };
      } catch (e) {
        if (e instanceof BuildGraphQLSchemaError) {
          return { error: e };
        } else {
          // rethrow unexpected errors
          throw e;
        }
      }
    }, [supergraphSchemaDocument]);

  return {
    schema,
    supergraphSchema,
    supergraphSchemaDocument,
    schemaDocument,
    createdAt: data?.service?.schema?.createdAt,
    hash: data?.service?.schema?.hash,
    loading,
    error: error || buildSchemaError || supergraphSchemaError,
  };
}
